import React from 'react';
import styled from 'styled-components';
import { tariffDepedencyStatus, useTariffQuantityRepricing } from '@src/domains/tariffs';
import { useIsSingleBookingPackageBooking, usePackageTimeOptions } from '@src/features/productPackage';
import { useAppSelector } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import { SellingMode } from '@src/core/buyerApiClient';
import {
  hasRepricingErrorSelector,
  isRepricingSelector,
  loadingAnyTariffVerificationsSelector,
  productPackageProductDiscountSelector,
  productSelector,
  repricingByTariffIdSelector,
  tariffsState,
} from '@src/stores/product';
import useScheduleTimeSelection from '@src/domains/products/hooks/useScheduleTimeSelection';
import ProductAdderFooterButtons from './ProductAdderFooterButtons';
import ProductAdderOngoingCartResume from './ProductAdderSummary';
import ProductAdderTariffList from './ProductAdderTariffList';
import TimeSelector from '@src/components/reusable/TimeSelector';
import { getSchedulingFrom } from '@src/common/seatMaps';
import { getSelectedTime } from '@src/domains/tariffs/core/tariffSelection';
import { isTotalQuantityExceedingWholeDateLimit } from '@src/domains/tariffs/core/tariffQuantityLimit';
import { useTariffSelection } from '@src/domains/tariffs/hooks/useTariffSelection';
import SeatsUnavailableAlert from './SeatsUnavailableAlert';
import useSelectedProductId from '@src/hooks/useSelectedProductId';
import usePackageIdParam from '@src/hooks/usePackageIdParam';

const ProductAdderTariff = ({ isMultiProductFlow = false }: { isMultiProductFlow?: boolean }) => {
  const widgetId = useWidgetId();
  const prodId = useSelectedProductId();
  useTariffQuantityRepricing(widgetId, prodId);
  const repricingByTariffId = useAppSelector(repricingByTariffIdSelector(widgetId));
  const hasRepricingError = useAppSelector(hasRepricingErrorSelector(widgetId));
  const isAnyRepricing = useAppSelector(isRepricingSelector(widgetId));
  const { listOfTariffsPerType } = useAppSelector(tariffsState(widgetId));
  const product = useAppSelector(productSelector(prodId))!;
  const loadingTariffVerifications = useAppSelector(loadingAnyTariffVerificationsSelector);
  const packageId = usePackageIdParam();
  const isSingleBooking = useIsSingleBookingPackageBooking();
  const {
    visible: packageTimeVisible,
    hoursList: packageHoursList,
    morningHours: packageMorningHours,
    lateHours: packageLateHours,
    allTimesUnavailable: packageAllTimesUnavailable,
  } = usePackageTimeOptions(packageId ?? '');
  const tariffPackageDiscount = useAppSelector(productPackageProductDiscountSelector(widgetId, prodId, packageId));

  const {
    hoursList,
    morningHours,
    lateHours,
    selectedTariffsPerType,
    areAllTariffsHoursUnavailable,
    selectedHour,
    onSelectHour: handleSelectHour,
    visible: productTimeVisible,
  } = useScheduleTimeSelection(isSingleBooking ? { hoursListOverride: packageHoursList } : undefined);

  // Shared tariff-selection orchestration. Aliased to the local names so the JSX is unchanged.
  // hoursList narrows the list for with_date_and_time products (v2 passes none).
  const {
    tariffs: tariffsExhibitedListSorted,
    acceptableQuantitiesFor: acceptableQuantities,
    priceInfoFor: tariffPriceInfo,
    descriptionFor: tariffDescription,
    isDependent: isTariffDependent,
    calculateTariffIndex,
    onUpdateQuantity: updateTariff,
    showSingleError: shouldDisplaySingleErrorLabel,
    showIndependentError: showIndependentTariffNotSelectedError,
    showMultipleError: shouldDisplayMultipleErrorLabel,
  } = useTariffSelection({ hoursList: isSingleBooking ? packageHoursList : hoursList });

  const selectedTime = getSelectedTime(selectedHour);
  const scheduling = getSchedulingFrom(product, selectedTariffsPerType, selectedTime);

  const isSellingModeWithDateOnly = product.sellingMode === SellingMode.WithDateOnly;
  const areAllQtdGreaterThanLimit = isTotalQuantityExceedingWholeDateLimit(listOfTariffsPerType);
  const hasNotTariffDependencyError = !tariffDepedencyStatus(listOfTariffsPerType, selectedHour)
    .hasTariffDependencyError;
  const timeVisible = isSingleBooking ? packageTimeVisible : productTimeVisible;
  const morningTimes = isSingleBooking ? packageMorningHours : morningHours;
  const nightTimes = isSingleBooking ? packageLateHours : lateHours;
  const allTimesUnavailable = isSingleBooking ? packageAllTimesUnavailable : areAllTariffsHoursUnavailable;

  return (
    <>
      <ProductAdderTariffContainer>
        <ProductAdderOngoingCartResume hideTariffs={true} />
        {!isSingleBooking ? (
          <ProductAdderTariffList
            tariffs={tariffsExhibitedListSorted}
            product={product}
            selectedMinutes={selectedHour?.minutes ?? null}
            repricingByTariffId={repricingByTariffId}
            tariffPackageDiscount={tariffPackageDiscount}
            hasQuantityLimitError={areAllQtdGreaterThanLimit && isSellingModeWithDateOnly}
            hasRepricingError={hasRepricingError}
            priceInfoFor={tariffPriceInfo}
            descriptionFor={tariffDescription}
            isDependent={isTariffDependent}
            calculateTariffIndex={calculateTariffIndex}
            onUpdateQuantity={updateTariff}
            acceptableQuantitiesFor={acceptableQuantities}
            showSingleError={shouldDisplaySingleErrorLabel}
            showIndependentError={showIndependentTariffNotSelectedError}
            showMultipleError={shouldDisplayMultipleErrorLabel}
          />
        ) : null}
        {hasNotTariffDependencyError ? (
          <TimeSelector
            morningTimes={morningTimes}
            nightTimes={nightTimes}
            onSelectTime={handleSelectHour}
            areAllTimesUnavailable={allTimesUnavailable}
            hidden={!timeVisible}
            selectedTime={selectedHour}
          />
        ) : null}

        {scheduling && <SeatsUnavailableAlert scheduling={scheduling} timeMinutes={selectedTime} />}
      </ProductAdderTariffContainer>
      <ProductAdderFooterButtons
        disableNextStepButton={loadingTariffVerifications || isAnyRepricing}
        isMultiProductFlow={isMultiProductFlow}
      />
    </>
  );
};

const ProductAdderTariffContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export default ProductAdderTariff;
